/**
 * userPushService — mirror of webpushService.js but targeting the
 * /api/push/* end-user endpoints. iter-169r.
 *
 * The browser-side wiring (SW registration, urlBase64ToUint8Array,
 * permission gating) is identical to the admin variant; only the
 * backend prefix differs.
 */
import { api } from "../lib/api";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function getSupportState() {
  if (typeof window === "undefined") return { supported: false, reason: "no-window" };
  if (!("serviceWorker" in navigator)) return { supported: false, reason: "no-service-worker" };
  if (!("PushManager" in window)) return { supported: false, reason: "no-push-manager" };
  if (!("Notification" in window)) return { supported: false, reason: "no-notification-api" };
  return { supported: true, permission: Notification.permission };
}

async function ensureRegistration() {
  if (!("serviceWorker" in navigator)) throw new Error("Service Worker no soportado");
  try { await navigator.serviceWorker.register("/sw.js"); } catch { /* idempotent */ }
  return navigator.serviceWorker.ready;
}

export async function getCurrentSubscription() {
  const s = getSupportState();
  if (!s.supported) return null;
  try {
    const reg = await ensureRegistration();
    return await reg.pushManager.getSubscription();
  } catch { return null; }
}

export async function enableUserPushNotifications() {
  const support = getSupportState();
  if (!support.supported) throw new Error("Tu navegador no soporta notificaciones push.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Permiso denegado. Habilitalo desde la configuración del navegador."
        : "No se otorgaron permisos.",
    );
  }
  const reg = await ensureRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { data } = await api.get("/push/public-key");
    if (!data?.publicKey) throw new Error("Falta la clave pública VAPID en el servidor.");
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }
  await api.post("/push/subscribe", sub.toJSON());
  return sub;
}

export async function disableUserPushNotifications() {
  const sub = await getCurrentSubscription();
  if (!sub) return { ok: true, deleted: 0, alreadyOff: true };
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch { /* ignore */ }
  const { data } = await api.post("/push/unsubscribe", { endpoint });
  return { ok: true, ...data };
}

export async function sendUserTestNotification() {
  const { data } = await api.post("/push/test", {});
  return data;
}

export async function getUserPushStatus() {
  const { data } = await api.get("/push/status");
  return data;
}
